import React, { Suspense } from 'react';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/UserAuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";

const Index = React.lazy(() => import("./pages/Index"));
const Produtos = React.lazy(() => import('./pages/Produtos'));
const Blog = React.lazy(() => import("./pages/Blog"));
const Privacy = React.lazy(() => import("./pages/Privacy"));
const Devolucoes = React.lazy(() => import("./pages/Devolucoes"));
const Termos = React.lazy(() => import("./pages/Termos"));
const Garantia = React.lazy(() => import("./pages/Garantia"));
const Cookies = React.lazy(() => import("./pages/Cookies"));
const NotFound = React.lazy(() => import("./pages/NotFound"));
const Parceiros = React.lazy(() => import("./pages/Parceiros"));

// Páginas da Telemedicina
const TelemedicineAuth = React.lazy(() => import("./pages/telemedicine/TelemedicineAuth"));
const TelemedicineDashboard = React.lazy(() => import("./pages/telemedicine/TelemedicineDashboard"));
const AddPet = React.lazy(() => import("./pages/telemedicine/AddPet"));
const PetProfile = React.lazy(() => import("./pages/telemedicine/PetProfile"));
const EditPet = React.lazy(() => import("./pages/telemedicine/EditPet"));
const CompleteGoogleProfile = React.lazy(() => import("./pages/telemedicine/CompleteGoogleProfile"));
const ScheduleAppointment = React.lazy(() => import('./pages/telemedicine/ScheduleAppointment'));
const PaymentSuccess = React.lazy(() => import('./pages/telemedicine/PaymentSuccess'));
const RecurringPlans = React.lazy(() => import('./pages/telemedicine/RecurringPlans'));
const SymptomTriage = React.lazy(() => import('./pages/telemedicine/SymptomTriage'));
const EducationalContent = React.lazy(() => import('./pages/telemedicine/EducationalContent'));
const PetFriendlyLocations = React.lazy(() => import('./pages/telemedicine/PetFriendlyLocations'));
const VeterinarianList = React.lazy(() => import('./pages/telemedicine/VeterinarianList'));
const VeterinarianRegister = React.lazy(() => import('./pages/telemedicine/VeterinarianRegister'));
const VeterinarianProfile = React.lazy(() => import('./pages/telemedicine/VeterinarianProfile'));
const EditVeterinarian = React.lazy(() => import('./pages/telemedicine/EditVeterinarian'));
const AdminPanel = React.lazy(() => import('./pages/telemedicine/AdminPanel'));
const ViewUser = React.lazy(() => import('./pages/telemedicine/ViewUser'));
const EditUser = React.lazy(() => import('./pages/telemedicine/EditUser'));
const VideoConsultation = React.lazy(() => import('./pages/telemedicine/VideoConsultation'));
const UserProfile = React.lazy(() => import('./pages/telemedicine/UserProfile'));
const HealthWallet = React.lazy(() => import('./pages/telemedicine/HealthWallet'));

// Portal do Parceiro
const PartnerDashboard = React.lazy(() => import('./pages/partner/PartnerDashboard'));
const PartnerServices = React.lazy(() => import('./pages/partner/PartnerServices'));
const PartnerAgenda = React.lazy(() => import('./pages/partner/PartnerAgenda'));

const queryClient = new QueryClient();

const PageLoader = () => (
  <div className="flex min-h-screen flex-col w-full bg-background animate-pulse">
    {/* Skeleton Header */}
    <div className="h-16 w-full border-b border-border flex items-center px-6">
      <div className="h-8 w-32 bg-muted rounded"></div>
      <div className="ml-auto flex gap-4">
        <div className="h-8 w-20 bg-muted rounded"></div>
        <div className="h-8 w-8 bg-muted rounded-full"></div>
      </div>
    </div>
    {/* Skeleton Body */}
    <div className="flex-1 container mx-auto p-6 space-y-6">
      <div className="h-10 w-1/3 bg-muted rounded"></div>
      <div className="space-y-3">
        <div className="h-4 w-full bg-muted rounded"></div>
        <div className="h-4 w-full bg-muted rounded"></div>
        <div className="h-4 w-3/4 bg-muted rounded"></div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-6">
        <div className="h-40 w-full bg-muted rounded-xl"></div>
        <div className="h-40 w-full bg-muted rounded-xl"></div>
        <div className="h-40 w-full bg-muted rounded-xl"></div>
      </div>
    </div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/produtos" element={<Produtos />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/privacidade" element={<Privacy />} />
              <Route path="/devolucoes" element={<Devolucoes />} />
              <Route path="/termos" element={<Termos />} />
              <Route path="/garantia" element={<Garantia />} />
              <Route path="/cookies" element={<Cookies />} />
              <Route path="/parceiros" element={<Parceiros />} />
              
              {/* Rotas da Telemedicina */}
              <Route path="/telemedicina/perfil" element={<UserProfile />} />
              <Route path="/telemedicina/auth" element={<TelemedicineAuth />} />
              <Route path="/telemedicina/complete-profile" element={<CompleteGoogleProfile />} />
              <Route 
                path="/telemedicina" 
                element={
                  <ProtectedRoute>
                    <TelemedicineDashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/telemedicina/perfil" 
                element={
                  <ProtectedRoute>
                    <UserProfile />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/telemedicina/pets/novo" 
                element={
                  <ProtectedRoute>
                    <AddPet />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/telemedicina/pets/:petId" 
                element={
                  <ProtectedRoute>
                    <PetProfile />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/telemedicina/pets/:petId/edit" 
                element={
                  <ProtectedRoute>
                    <EditPet />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/telemedicina/pets/:petId/health" 
                element={
                  <ProtectedRoute>
                    <HealthWallet />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/telemedicina/agendar" 
                element={
                  <ProtectedRoute>
                    <ScheduleAppointment />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/telemedicina/triagem" 
                element={
                  <ProtectedRoute>
                    <SymptomTriage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/telemedicina/educativo" 
                element={
                  <ProtectedRoute>
                    <EducationalContent />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/telemedicina/planos-preventivos" 
                element={
                  <ProtectedRoute>
                    <RecurringPlans />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/telemedicina/locais" 
                element={
                  <ProtectedRoute>
                    <PetFriendlyLocations />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/telemedicina/veterinarios" 
                element={
                  <ProtectedRoute>
                    <VeterinarianList />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/telemedicina/veterinarios/cadastrar" 
                element={
                  <ProtectedRoute>
                    <VeterinarianRegister />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/telemedicina/veterinarios/:id" 
                element={
                  <ProtectedRoute>
                    <VeterinarianProfile />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/telemedicina/veterinarios/:id/editar" 
                element={
                  <ProtectedRoute>
                    <EditVeterinarian />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/telemedicina/admin" 
                element={
                  <ProtectedRoute>
                    <AdminPanel />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/telemedicina/admin/users/:userId/view" 
                element={
                  <ProtectedRoute>
                    <ViewUser />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/telemedicina/admin/users/:userId/edit" 
                element={
                  <ProtectedRoute>
                    <EditUser />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/telemedicina/consulta/:consultationId" 
                element={
                  <ProtectedRoute>
                    <VideoConsultation />
                  </ProtectedRoute>
                } 
              />
  
              <Route 
                path="/parceiros/dashboard" 
                element={
                  <ProtectedRoute>
                    <PartnerDashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/parceiros/servicos" 
                element={
                  <ProtectedRoute>
                    <PartnerServices />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/parceiros/agenda" 
                element={
                  <ProtectedRoute>
                    <PartnerAgenda />
                  </ProtectedRoute>
                } 
              />
              
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
